import React, { useMemo, useState } from "react";
import toast from "react-hot-toast";
import { fetchCallRecordings } from "js/Api/index";
import { fetchOrganizationDevices } from "js/Api/devices";
import {
    transformSingleDeviceCallLogs,
    exportCallLogsToExcel,
    parseStartTimeToDateForCompare,
    getTimeZoneOptions,
    getBrowserTimeZone,
} from "./callLogsExportUtils";

function CallLogsBulkExport({ userInfo, onBack }) {
    const [orgCode, setOrgCode] = useState("");
    const [devices, setDevices] = useState([]);
    const [selectedDeviceIds, setSelectedDeviceIds] = useState([]);
    const [loadingDevices, setLoadingDevices] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [selectedTimeZone, setSelectedTimeZone] = useState(getBrowserTimeZone());
    const [progress, setProgress] = useState({
        current: 0,
        total: 0,
        currentDeviceName: "",
        totalLogs: 0,
        failedDevices: [],
    });

    const allSelected = useMemo(
        () => devices.length > 0 && selectedDeviceIds.length === devices.length,
        [devices, selectedDeviceIds]
    );

    const timeZoneOptions = useMemo(() => getTimeZoneOptions(), []);

    const handleLoadDevices = async () => {
        if (!orgCode.trim()) {
            toast.error("Please input org code");
            return;
        }

        setLoadingDevices(true);
        setDevices([]);
        setSelectedDeviceIds([]);

        try {
            const result = await fetchOrganizationDevices(orgCode.trim(), false);

            if (!result.success) {
                throw new Error(result.error || "Failed to load organization devices");
            }

            const rawRows = Array.isArray(result.data) ? result.data : [];
            const rows = rawRows.map((d) => ({
                id: d.id || d.Id,
                accountId: d.accountId || d.AccountId,
                name: d.name || d.Name,
                serialNo: d.serialNo || d.SerialNo,
                orgCode: d.orgCode || d.OrgCode,
                deviceType: d.deviceType || d.DeviceType,
                userEmail: d.userEmail || d.UserEmail,
            }));

            setDevices(rows);
            setSelectedDeviceIds(rows.map((d) => d.id));

            toast.success(`Loaded ${rows.length} device(s)`);
        } catch (error) {
            console.error(error);
            toast.error(error.message || "Failed to load organization devices");
        } finally {
            setLoadingDevices(false);
        }
    };

    const toggleDevice = (deviceId) => {
        setSelectedDeviceIds((prev) =>
            prev.includes(deviceId)
                ? prev.filter((id) => id !== deviceId)
                : [...prev, deviceId]
        );
    };

    const handleSelectAll = () => {
        setSelectedDeviceIds(devices.map((d) => d.id));
    };

    const handleUnselectAll = () => {
        setSelectedDeviceIds([]);
    };

    const handleExport = async () => {
        if (!userInfo?.userId) {
            toast.error("Current user info is missing");
            return;
        }

        const selectedDevices = devices.filter((d) => selectedDeviceIds.includes(d.id));

        if (selectedDevices.length === 0) {
            toast.error("Please select at least one device");
            return;
        }

        if (!selectedTimeZone) {
            toast.error("Please select a time zone");
            return;
        }

        setExporting(true);
        setProgress({
            current: 0,
            total: selectedDevices.length,
            currentDeviceName: "",
            totalLogs: 0,
            failedDevices: [],
        });

        const mergedLogs = [];
        const failedDevices = [];

        try {
            for (let i = 0; i < selectedDevices.length; i++) {
                const device = selectedDevices[i];

                setProgress((prev) => ({
                    ...prev,
                    current: i + 1,
                    currentDeviceName: device.name || device.id,
                }));

                try {
                    const result = await fetchCallRecordings(device.id, userInfo.userId);

                    if (!result?.success) {
                        failedDevices.push({
                            id: device.id,
                            name: device.name,
                            error: result?.error || "Failed to fetch call logs",
                        });
                        continue;
                    }

                    const transformed = transformSingleDeviceCallLogs(
                        result.data || [],
                        device
                    );

                    // 每个设备内部按时间排序，设备之间保持分组顺序
                    const deviceLogsSorted = [...transformed].sort((a, b) => {
                        const at =
                            parseStartTimeToDateForCompare(a.startTime)?.getTime() ?? 0;
                        const bt =
                            parseStartTimeToDateForCompare(b.startTime)?.getTime() ?? 0;
                        return bt - at;
                    });

                    mergedLogs.push(...deviceLogsSorted);

                    setProgress((prev) => ({
                        ...prev,
                        totalLogs: mergedLogs.length,
                    }));
                } catch (err) {
                    console.error(`Failed for device ${device.id}`, err);
                    failedDevices.push({
                        id: device.id,
                        name: device.name,
                        error: err?.message || "Unknown error",
                    });
                }
            }

            await exportCallLogsToExcel(
                mergedLogs,
                `${orgCode.trim()}_${selectedTimeZone.replaceAll("/", "-")}_${new Date()
                    .toISOString()
                    .slice(0, 10)}`,
                selectedTimeZone
            );

            if (failedDevices.length > 0) {
                toast.success(
                    `Exported ${mergedLogs.length} row(s). ${failedDevices.length} device(s) failed.`
                );
            } else {
                toast.success(`Exported ${mergedLogs.length} row(s).`);
            }

            setProgress((prev) => ({
                ...prev,
                failedDevices,
            }));
        } catch (error) {
            console.error(error);
            toast.error(error.message || "Export failed");
        } finally {
            setExporting(false);
        }
    };

    return (
        <div>
            <div
                style={{
                    marginBottom: "24px",
                    padding: "20px",
                    backgroundColor: "#f8f9fa",
                    borderRadius: "8px",
                }}
            >
                <div className="row">
                    <div className="col-md-5">
                        <label style={{ fontWeight: "bold", marginBottom: "8px" }}>
                            Organization Code
                        </label>
                        <input
                            type="text"
                            className="form-control"
                            value={orgCode}
                            onChange={(e) => setOrgCode(e.target.value)}
                            placeholder="Enter org code"
                            disabled={loadingDevices || exporting}
                        />
                        <small className="form-text text-muted">
                            This loads all devices using include_profile_picture=false.
                        </small>
                    </div>

                    <div className="col-md-4">
                        <label style={{ fontWeight: "bold", marginBottom: "8px" }}>
                            Export Time Zone
                        </label>
                        <select
                            className="form-control"
                            value={selectedTimeZone}
                            onChange={(e) => setSelectedTimeZone(e.target.value)}
                            disabled={loadingDevices || exporting}
                        >
                            {timeZoneOptions.map((tz) => (
                                <option key={tz} value={tz}>
                                    {tz}
                                </option>
                            ))}
                        </select>
                        <small className="form-text text-muted">
                            Date and time in Excel will use this time zone.
                        </small>
                    </div>

                    <div className="col-md-3">
                        <div style={{ marginTop: "30px", display: "flex", gap: "8px" }}>
                            <button
                                className="btn btn-primary"
                                onClick={handleLoadDevices}
                                disabled={loadingDevices || exporting}
                            >
                                {loadingDevices ? "Loading..." : "Load Devices"}
                            </button>

                            {onBack && (
                                <button
                                    className="btn btn-secondary"
                                    onClick={onBack}
                                    disabled={loadingDevices || exporting}
                                >
                                    Back
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {devices.length > 0 && (
                <>
                    <div
                        style={{
                            marginBottom: "16px",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            gap: "12px",
                            flexWrap: "wrap",
                        }}
                    >
                        <div>
                            <strong>{devices.length}</strong> device(s) loaded,{" "}
                            <strong>{selectedDeviceIds.length}</strong> selected
                        </div>

                        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                            <button
                                className="btn btn-secondary"
                                onClick={allSelected ? handleUnselectAll : handleSelectAll}
                                disabled={exporting}
                            >
                                {allSelected ? "Unselect All" : "Select All"}
                            </button>

                            <button
                                className="btn btn-success"
                                onClick={handleExport}
                                disabled={exporting || selectedDeviceIds.length === 0}
                            >
                                {exporting ? "Exporting..." : "Export Selected Call Logs"}
                            </button>
                        </div>
                    </div>

                    {exporting && (
                        <div
                            style={{
                                marginBottom: "16px",
                                padding: "12px",
                                backgroundColor: "#fff3cd",
                                borderRadius: "6px",
                            }}
                        >
                            <div>
                                Processing {progress.current} / {progress.total}
                            </div>
                            <div>Current device: {progress.currentDeviceName || "-"}</div>
                            <div>Total merged logs: {progress.totalLogs}</div>
                            <div>Export time zone: {selectedTimeZone}</div>
                        </div>
                    )}

                    <div className="table-responsive">
                        <table className="table table-hover table-bordered">
                            <thead className="thead-light">
                                <tr>
                                    <th style={{ width: "60px" }}>Select</th>
                                    <th>Device ID</th>
                                    <th>Name</th>
                                    <th>Serial No</th>
                                    <th>Account ID</th>
                                    <th>Email</th>
                                    <th>Device Type</th>
                                    <th>Org Code</th>
                                </tr>
                            </thead>
                            <tbody>
                                {devices.map((device) => (
                                    <tr key={device.id}>
                                        <td className="text-center">
                                            <input
                                                type="checkbox"
                                                checked={selectedDeviceIds.includes(device.id)}
                                                onChange={() => toggleDevice(device.id)}
                                                disabled={exporting}
                                            />
                                        </td>
                                        <td style={{ fontFamily: "monospace", fontSize: "12px" }}>
                                            {device.id}
                                        </td>
                                        <td>{device.name || "N/A"}</td>
                                        <td>{device.serialNo || "N/A"}</td>
                                        <td style={{ fontFamily: "monospace", fontSize: "12px" }}>
                                            {device.accountId || "N/A"}
                                        </td>
                                        <td>{device.userEmail || "N/A"}</td>
                                        <td>{device.deviceType || "N/A"}</td>
                                        <td>{device.orgCode || "N/A"}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {progress.failedDevices?.length > 0 && !exporting && (
                        <div
                            style={{
                                marginTop: "16px",
                                padding: "12px",
                                backgroundColor: "#f8d7da",
                                borderRadius: "6px",
                            }}
                        >
                            <strong>Failed devices:</strong>
                            <ul style={{ marginTop: "8px", marginBottom: 0 }}>
                                {progress.failedDevices.map((d) => (
                                    <li key={d.id}>
                                        {d.name || d.id}: {d.error}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

export default CallLogsBulkExport;